"use client";

import { c as _c } from "react/compiler-runtime";
import { GET_USER_QUERY, LOGIN_MUTATION, SEND_EMAIL_OTP_MUTATION, SEND_OTP_MUTATION, VERIFY_EMAIL_OTP_MUTATION, VERIFY_OTP_MUTATION, type AuthParams, type LoginResponse, type SendOtpResponse, type User, type VerifyOtpResponse } from "@/gql/auth";
import { useSelectedGroupSync } from "@/hooks/use-selected-group-sync";
import { useAuthStore, authStorage } from "@/stores/auth-store";
import { trackLogin } from "@/utils/analytics/track-login";
import { logger } from "@/utils/logger";
import { useApolloClient, useMutation } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// AuthHydrator — thin component that triggers store initialization on mount.
// Replaces the old AuthProvider context wrapper.
// ---------------------------------------------------------------------------

export function AuthHydrator() {
  const $ = _c(3);
  const initializeAuth = useAuthStore(_temp);
  const initialized = useRef(false);
  useSelectedGroupSync();
  let t0;
  let t1;
  if ($[0] !== initializeAuth) {
    t0 = () => {
      if (!initialized.current) {
        initialized.current = true;
        initializeAuth();
      }
    };
    t1 = [initializeAuth];
    $[0] = initializeAuth;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}

// ---------------------------------------------------------------------------
// OTP operations (Apollo-dependent, must remain as React hook)
// ---------------------------------------------------------------------------
function _temp(state) {
  return state.initializeAuth;
}
function useOtpOperations() {
  const [sendOtpMutation, {
    loading: sendOtpLoading
  }] = useMutation<{
    auth: SendOtpResponse;
  }, {
    params: AuthParams;
  }>(SEND_OTP_MUTATION);
  const [sendEmailOtpMutation, {
    loading: sendEmailOtpLoading
  }] = useMutation<{
    auth: SendOtpResponse;
  }, {
    params: AuthParams;
  }>(SEND_EMAIL_OTP_MUTATION);
  const [verifyOtpMutation, {
    loading: verifyOtpLoading
  }] = useMutation<{
    auth: VerifyOtpResponse;
  }, {
    params: AuthParams;
  }>(VERIFY_OTP_MUTATION);
  const [verifyOtpMailMutation, {
    loading: verifyOtpMailLoading
  }] = useMutation<{
    auth: VerifyOtpResponse;
  }, {
    params: AuthParams;
  }>(VERIFY_EMAIL_OTP_MUTATION);
  const sendOtp = useCallback(async (phone: string, resend = false, method: "sms" | "whatsapp" = "whatsapp"): Promise<boolean> => {
    try {
      const {
        data
      } = await sendOtpMutation({
        variables: {
          params: {
            sendOtp: {
              phone,
              resend,
              purpose: "__login__",
              sendHashCodes: true,
              whatsapp: method === "whatsapp"
            }
          }
        }
      });
      const success = data?.auth?.sendOtp ?? false;
      return success;
    } catch (error) {
      logger.debug("Failed to send OTP - user can retry:", error);
      return false;
    }
  }, [sendOtpMutation]);
  const sendOtpMail = useCallback(async (email: string, name: string, resend_0 = false): Promise<boolean> => {
    try {
      const {
        data: data_0
      } = await sendEmailOtpMutation({
        variables: {
          params: {
            sendOtpMail: {
              email,
              name,
              resend: resend_0
            }
          }
        }
      });
      const success_0 = data_0?.auth?.sendOtpMail ?? false;
      return success_0;
    } catch (error_0) {
      logger.debug("Failed to send OTP - user can retry:", error_0);
      return false;
    }
  }, [sendEmailOtpMutation]);
  const verifyOtp = useCallback(async (phone_0: string, otp: string, purpose = "__login__"): Promise<boolean> => {
    try {
      const {
        data: data_1
      } = await verifyOtpMutation({
        variables: {
          params: {
            verifyOtp: {
              phone: phone_0,
              otp,
              purpose
            }
          }
        }
      });
      const success_1 = data_1?.auth?.verifyOtp ?? false;
      return success_1;
    } catch (error_1) {
      logger.debug("Failed to verify OTP - user can retry:", error_1);
      return false;
    }
  }, [verifyOtpMutation]);
  const verifyOtpMail = useCallback(async (email_0: string, otp_0: string, url = "webApp"): Promise<boolean> => {
    try {
      const {
        data: data_2
      } = await verifyOtpMailMutation({
        variables: {
          params: {
            verifyOtpMail: {
              email: email_0,
              otp: otp_0,
              url
            }
          }
        }
      });
      const success_2 = data_2?.auth?.verifyOtpMail ?? false;
      return success_2;
    } catch (error_2) {
      logger.debug("Failed to verify OTP - user can retry:", error_2);
      return false;
    }
  }, [verifyOtpMailMutation]);
  return {
    sendOtp,
    sendOtpMail,
    verifyOtp,
    verifyOtpMail,
    isLoading: sendOtpLoading || verifyOtpLoading || sendEmailOtpLoading || verifyOtpMailLoading
  };
}

// ---------------------------------------------------------------------------
// Login operations (Apollo-dependent, must remain as React hook)
// ---------------------------------------------------------------------------

function useLoginOperations() {
  const [loginMutation, {
    loading: loginLoading
  }] = useMutation<{
    auth: {
      login: LoginResponse;
    };
  }, {
    params: AuthParams;
  }>(LOGIN_MUTATION);
  const login = useCallback(async (phone: string, otp: string): Promise<LoginResponse | null> => {
    try {
      const {
        data
      } = await loginMutation({
        variables: {
          params: {
            login: {
              phone,
              otp
            }
          }
        }
      });
      const loginData = data?.auth?.login;
      return loginData || null;
    } catch (error) {
      logger.error("Login failed:", error);
      return null;
    }
  }, [loginMutation]);
  const logout = useCallback(async (): Promise<void> => {
    try {
      // For now, just clear local storage
      // The server logout mutation might not be properly configured
    } catch (error_0) {
      logger.error("Logout failed:", error_0);
    }
  }, []);
  return {
    login,
    logout,
    isLoading: loginLoading
  };
}

// ---------------------------------------------------------------------------
// Main auth hook — combines Zustand state with Apollo operations
// ---------------------------------------------------------------------------

export function useAuth() {
  // Read core auth state from Zustand store
  const isHydrated = useAuthStore(state => state.isHydrated);
  const storeIsAuthenticated = useAuthStore(state_0 => state_0.isAuthenticated);
  const storeUser = useAuthStore(state_1 => state_1.user);
  const storeToken = useAuthStore(state_2 => state_2.token);
  const setAuthData = useAuthStore(state_3 => state_3.setAuthData);
  const clearAuthData = useAuthStore(state_4 => state_4.clearAuthData);

  // Apollo-dependent operations
  const otpOps = useOtpOperations();
  const {
    login,
    logout,
    isLoading: loginLoading
  } = useLoginOperations();
  const [error, setError] = useState<string | null>(null);
  const apolloClient = useApolloClient();

  // Combined login function that handles both login and state update
  const handleLogin = useCallback(async (phone: string, otp: string): Promise<LoginResponse | null> => {
    try {
      setError(null);
      const loginData = await login(phone, otp);
      if (loginData?.tokenData?.token && loginData?.user) {
        await setAuthData(loginData.tokenData.token, loginData.user);

        // Track successful phone OTP login
        try {
          await trackLogin({
            userData: {
              id: loginData.user.id,
              name: loginData.user.name,
              profile: loginData.user.profile,
              emails: loginData.user.emails,
              phones: loginData.user.phones,
              groups: loginData.user.groups,
              internal: Boolean(loginData.user.internal)
            },
            loginType: "phone_otp"
          });
        } catch (trackingError) {
          // Don't fail login if tracking fails
          logger.debug("Login tracking failed (non-critical):", trackingError);
        }
        return loginData;
      } else {
        const errorMsg_0 = "Invalid login response";
        setError(errorMsg_0);
        return null;
      }
    } catch (error_0) {
      const errorMsg = error_0 instanceof Error ? error_0.message : "Login failed";
      logger.error("Login error:", errorMsg);
      setError(errorMsg);
      return null;
    }
  }, [login, setAuthData]);

  // Combined logout function
  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await logout();
    } catch (error_1) {
      logger.error("Logout error:", error_1);
    } finally {
      await clearAuthData();
    }
  }, [logout, clearAuthData]);
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  const refetchUser = useCallback(async (user: User) => {
    const token = authStorage.getToken();
    if (token) {
      await authStorage.setAuth(token, user);
      await setAuthData(token, user);
    }
  }, [setAuthData]);
  const isValidToken = (str?: string) => str && str?.length > 16 && str.length < 50 && /^[a-zA-Z0-9+/=_-]+$/.test((str || "").trim());

  /**
   * Fetches fresh user data from the server and updates the cache.
   * Useful when cached user data becomes stale (e.g., group was deleted).
   * Returns the fresh user data or null if fetch fails.
   */
  const fetchFreshUserData = useCallback(async (authToken?: string): Promise<User | null> => {
    if (authToken && isValidToken(authToken) && !authStorage.getToken()) {
      localStorage.setItem("settlin_auth_token", authToken);
    }
    const token_0 = authStorage.getToken();
    try {
      const {
        data
      } = await apolloClient.query<{
        auth: {
          user: User;
        };
      }>({
        query: GET_USER_QUERY,
        variables: {
          params: {
            user: {}
          }
        },
        fetchPolicy: "network-only"
      });
      const freshUser = data?.auth?.user;
      if (freshUser) {
        if (token_0) {
          await authStorage.setAuth(token_0, freshUser);
          await setAuthData(token_0, freshUser);
          logger.info("Successfully refreshed user data from server");
        }
        return freshUser;
      }
      return null;
    } catch (err) {
      logger.error("Failed to fetch fresh user data:", err);
      return null;
    }
  }, [apolloClient, setAuthData]);

  // Don't show authenticated state until hydrated
  const isAuthenticated = isHydrated ? storeIsAuthenticated : false;
  const user_0 = isHydrated ? storeUser : null;

  /**
   * isLoading: Tracks initial auth hydration (checking localStorage/IndexedDB for existing session)
   * - Used by protected routes to show loading state during app initialization
   * - Only true during first mount, becomes false once hydration completes
   *
   * isLoginStateLoading: Tracks active login/OTP operations
   * - Used by login forms to show loading spinners during user login actions
   * - True while sendOtp, verifyOtp, or login mutations are in progress
   */
  const isLoading = !isHydrated;
  const isLoginStateLoading = otpOps.isLoading || loginLoading;
  return {
    // State
    isAuthenticated,
    user: user_0,
    token: storeToken,
    isLoading,
    isLoginStateLoading,
    error,
    // Actions
    sendOtp: otpOps.sendOtp,
    sendOtpMail: otpOps.sendOtpMail,
    login: handleLogin,
    verifyOtp: otpOps.verifyOtp,
    verifyOtpMail: otpOps.verifyOtpMail,
    logout: handleLogout,
    clearError,
    refetchUser,
    fetchFreshUserData
  };
}